import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { io, Socket } from 'socket.io-client';
import API_BASE_URL, { WS_URL } from '../config/api';

interface HealthStatus {
  frontend: {
    status: string;
    timestamp: string;
    version: string;
  };
  backend: {
    status: string;
    message?: string;
    uptime?: number;
    error?: string;
  };
  websocket: {
    status: string;
    latency?: number;
    error?: string;
  };
}

const HealthCheck: React.FC = () => {
  const { t } = useTranslation();
  const [healthStatus, setHealthStatus] = useState<HealthStatus>({
    frontend: {
      status: 'ok',
      timestamp: new Date().toISOString(),
      version: process.env.REACT_APP_VERSION || '1.0.0'
    },
    backend: {
      status: 'checking'
    },
    websocket: {
      status: 'checking'
    }
  });

  useEffect(() => {
    const checkBackendHealth = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/health`);
        const data = await response.json();

        setHealthStatus(prev => ({
          ...prev,
          backend: {
            status: 'ok',
            message: data.message,
            uptime: data.uptime
          }
        }));
      } catch (error) {
        setHealthStatus(prev => ({
          ...prev,
          backend: {
            status: 'error',
            error: error instanceof Error ? error.message : 'Unknown error'
          }
        }));
      }
    };

    let currentSocket: Socket | null = null;
    let timeoutId: NodeJS.Timeout | null = null;

    const checkWebSocketHealth = () => {
      // Clean up previous socket if exists
      if (currentSocket) {
        currentSocket.removeAllListeners();
        currentSocket.disconnect();
        currentSocket = null;
      }

      // Clear previous timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      if (!WS_URL) {
        setHealthStatus(prev => ({
          ...prev,
          websocket: {
            status: 'error',
            error: 'WebSocket URL not configured'
          }
        }));
        return;
      }

      const startTime = Date.now();
      let hasConnected = false;

      timeoutId = setTimeout(() => {
        if (currentSocket && !hasConnected) {
          currentSocket.removeAllListeners();
          currentSocket.disconnect();
          currentSocket = null;
          setHealthStatus(prev => ({
            ...prev,
            websocket: {
              status: 'error',
              error: 'Connection timeout'
            }
          }));
        }
      }, 5000);

      try {
        currentSocket = io(WS_URL, {
          transports: ['polling', 'websocket'],
          reconnection: false,
          timeout: 5000,
          forceNew: true
        });
        
        currentSocket.on('connect', () => {
          hasConnected = true;
          const latency = Date.now() - startTime;
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
          setHealthStatus(prev => ({
            ...prev,
            websocket: {
              status: 'ok',
              latency
            }
          }));

          // Disconnect after successful check
          setTimeout(() => {
            if (currentSocket) {
              currentSocket.removeAllListeners();
              currentSocket.disconnect();
              currentSocket = null;
            }
          }, 100);
        });

        currentSocket.on('connect_error', (error) => {
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
          setHealthStatus(prev => ({
            ...prev,
            websocket: {
              status: 'error',
              error: `Connection failed: ${error.message}`
            }
          }));
          if (currentSocket) {
            currentSocket.removeAllListeners();
            currentSocket.disconnect();
            currentSocket = null;
          }
        });
      } catch (error) {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        setHealthStatus(prev => ({
          ...prev,
          websocket: {
            status: 'error',
            error: error instanceof Error ? error.message : 'Unknown error'
          }
        }));
      }
    };

    checkBackendHealth();
    checkWebSocketHealth();

    const interval = setInterval(() => {
      checkBackendHealth();
      checkWebSocketHealth();
    }, 30000);

    return () => {
      clearInterval(interval);
      if (currentSocket) {
        currentSocket.removeAllListeners();
        currentSocket.disconnect();
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ok':
        return 'text-green-600';
      case 'checking':
        return 'text-yellow-600';
      case 'error':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusBgColor = (status: string) => {
    switch (status) {
      case 'ok':
        return 'bg-green-100';
      case 'checking':
        return 'bg-yellow-100';
      case 'error':
        return 'bg-red-100';
      default:
        return 'bg-gray-100';
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">Health Check</h1>

        {/* Frontend Status */}
        <div className={`mb-6 p-4 rounded-lg ${getStatusBgColor(healthStatus.frontend.status)}`}>
          <h2 className="text-xl font-semibold mb-3 text-gray-800">Frontend Status</h2>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="font-medium">Status:</span>
              <span className={`font-bold ${getStatusColor(healthStatus.frontend.status)}`}>
                {healthStatus.frontend.status.toUpperCase()}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Timestamp:</span>
              <span className="text-gray-700">
                {new Date(healthStatus.frontend.timestamp).toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Version:</span>
              <span className="text-gray-700">{healthStatus.frontend.version}</span>
            </div>
          </div>
        </div>

        {/* Backend Status */}
        <div className={`mb-6 p-4 rounded-lg ${getStatusBgColor(healthStatus.backend.status)}`}>
          <h2 className="text-xl font-semibold mb-3 text-gray-800">Backend Status</h2>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="font-medium">Status:</span>
              <span className={`font-bold ${getStatusColor(healthStatus.backend.status)}`}>
                {healthStatus.backend.status.toUpperCase()}
              </span>
            </div>

            {healthStatus.backend.message && (
              <div className="flex justify-between">
                <span className="font-medium">Message:</span>
                <span className="text-gray-700">{healthStatus.backend.message}</span>
              </div>
            )}

            {healthStatus.backend.uptime !== undefined && (
              <div className="flex justify-between">
                <span className="font-medium">Uptime:</span>
                <span className="text-gray-700">
                  {Math.floor(healthStatus.backend.uptime)} seconds
                </span>
              </div>
            )}

            {healthStatus.backend.error && (
              <div className="mt-2">
                <span className="font-medium text-red-700">Error:</span>
                <p className="text-red-600 text-sm mt-1">{healthStatus.backend.error}</p>
              </div>
            )}
          </div>
        </div>

        {/* WebSocket Status */}
        <div className={`p-4 rounded-lg ${getStatusBgColor(healthStatus.websocket.status)}`}>
          <h2 className="text-xl font-semibold mb-3 text-gray-800">WebSocket Status</h2>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="font-medium">Status:</span>
              <span className={`font-bold ${getStatusColor(healthStatus.websocket.status)}`}>
                {healthStatus.websocket.status.toUpperCase()}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="font-medium">URL:</span>
              <span className="text-gray-700 text-sm break-all">
                {WS_URL || 'Not configured'}
              </span>
            </div>

            {healthStatus.websocket.latency !== undefined && (
              <div className="flex justify-between">
                <span className="font-medium">Latency:</span>
                <span className="text-gray-700">
                  {healthStatus.websocket.latency} ms
                </span>
              </div>
            )}

            {healthStatus.websocket.error && (
              <div className="mt-2">
                <span className="font-medium text-red-700">Error:</span>
                <p className="text-red-600 text-sm mt-1">{healthStatus.websocket.error}</p>
              </div>
            )}
          </div>
        </div>

        {/* Overall Status */}
        <div className="mt-6 text-center">
          {healthStatus.frontend.status === 'ok' &&
           healthStatus.backend.status === 'ok' &&
           healthStatus.websocket.status === 'ok' ? (
            <div className="text-green-600 font-bold text-lg">
              ✅ All systems operational
            </div>
          ) : (
            <div className="text-yellow-600 font-bold text-lg">
              ⚠️ Some systems may be experiencing issues
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HealthCheck;